import type { Schema } from '@tix/openapi-sdk'

/** API row from `review_summary` plus UI-only `editable` (derived from flow `previous_inputs`). */
export type SummaryRow = Schema<'review_summary'>['rows'][number] & { editable: boolean }

/**
 * One rendered block in the confirm step: either a single row or a **contiguous** run of rows
 * that share the same `group_id` (see `groupConsecutiveReviewRows`).
 */
export type SummaryGroup = {
  rows: SummaryRow[]
  /** Resolver id passed to `goBackTo` when the user clicks Edit. */
  editTargetResolverId: string
  showEdit: boolean
}

/**
 * Walks `rows` left-to-right and builds groups for layout + a single Edit control per block.
 *
 * **Why adjacent runs only (not “merge every row with the same `group_id`”)?**
 *
 * In **tix-api**, `review_summary.rows` is built in resolver-tree **pre-order** (`buildReviewSummary` in
 * `flows/resolvers/resolver_confirm.go`). Row order reflects visit order, not a second pass grouped by
 * `group_id`. Each row’s `group_id` is set by `findGroupID`: the closest ancestor `pick_n` / `group` for
 * that resolver — i.e. metadata for **which parent owns the line** and where Edit should go, not an
 * instruction to reorder the list. Other rows (e.g. global variants, siblings outside that `pick_n`)
 * legitimately appear **between** lines that happen to share a parent id in edge cases; collapsing
 * non-adjacent same-`group_id` rows would reorder the summary relative to the API.
 *
 * So: merge **consecutive** same `group_id` to show one card + one Edit for one contiguous pick_n block;
 * keep non-adjacent runs as separate blocks so display order stays aligned with the server.
 *
 * - Rows **without** `group_id` are never merged; each becomes its own group.
 * - Rows **with** the same non-null `group_id` are merged only while `group_id` matches on consecutive indices.
 */
export function groupConsecutiveReviewRows(rows: SummaryRow[]): SummaryGroup[] {
  const groups: SummaryGroup[] = []

  let start = 0
  while (start < rows.length) {
    const row = rows[start]!
    const groupId = row.group_id ?? null

    if (groupId == null) {
      groups.push(makeSummaryGroup([row]))
      start += 1
      continue
    }

    let end = start + 1
    while (end < rows.length && rows[end]!.group_id === groupId) {
      end += 1
    }
    groups.push(makeSummaryGroup(rows.slice(start, end)))
    start = end
  }

  return groups
}

/**
 * Builds the metadata for one summary block (one card in the confirm UI).
 *
 * **Edit target** (matches tix-api semantics: `group_id` = ancestor `pick_n` / `group` resolver)
 * - When every row in the block shares the same non-null `group_id`, Edit uses that id (parent step).
 * - Otherwise (including a lone ungrouped row), Edit uses the first row's `resolver_id`.
 *
 * **Visibility**
 * - `showEdit` is true if any row in the block is marked `editable`.
 */
export function makeSummaryGroup(rows: SummaryRow[]): SummaryGroup {
  const first = rows[0]!
  const sharedGroupId = first.group_id ?? null
  const allSameGroup = sharedGroupId != null && rows.every((r) => r.group_id === sharedGroupId)
  const editTargetResolverId = allSameGroup ? sharedGroupId : first.resolver_id

  return {
    rows,
    editTargetResolverId,
    showEdit: rows.some((r) => r.editable),
  }
}
